import HeliusGate from "../components/HeliusGate";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  WalletIcon, PlusIcon, TrashIcon, ArrowUpRightIcon, ArrowDownRightIcon,
  ArrowSquareOutIcon, SpinnerGapIcon, LockKeyIcon, UsersThreeIcon, ChartBarIcon, CaretDownIcon, ChartLineUpIcon, WarningIcon,
} from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { useAuth } from "../context/AuthContext";
import {
  fetchSmartMoneyConfig, fetchTrackedWallets, addTrackedWallet, removeTrackedWallet, fetchWalletFeed, fetchWalletPnl,
} from "../lib/api";
import { QuickBuyLink } from "../components/TradeButton";

const timeAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const short = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;

const pnlScoreColor = (s) => (s >= 60 ? "#00E599" : s >= 35 ? "#F5A623" : "#FF3366");

const WalletPnL = ({ clientId, address }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["walletPnl", clientId, address],
    queryFn: () => fetchWalletPnl(clientId, address),
    staleTime: 30 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground text-xs font-mono">
        <SpinnerGapIcon size={16} className="animate-spin text-neon" /> Analyzing on-chain history…
      </div>
    );
  }
  if (isError || !data) {
    return <div className="py-3 text-center text-xs text-danger font-mono">Couldn't compute PnL.</div>;
  }
  if (data.closedPositions === 0) {
    return <div className="py-3 text-center text-xs text-muted-foreground font-mono">No closed trades in recent history to score.</div>;
  }
  const realizedColor = data.totalRealizedSol >= 0 ? "#00E599" : "#FF3366";
  const totalColor = data.totalPnlSol >= 0 ? "#00E599" : "#FF3366";
  const unrealColor = data.unrealizedSol >= 0 ? "#00E599" : "#FF3366";
  return (
    <div className="pt-3 space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="font-mono font-bold text-xl" style={{ color: pnlScoreColor(data.score) }}>{data.score}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Score</div>
        </div>
        <div className="text-center">
          <div className="font-mono font-bold text-xl">{data.winRate}%</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Win rate</div>
        </div>
        <div className="text-center">
          <div className="font-mono font-bold text-xl" style={{ color: totalColor }}>
            {data.totalPnlSol >= 0 ? "+" : ""}{data.totalPnlSol}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total SOL</div>
        </div>
      </div>
      <div className="flex justify-center gap-3 text-xs font-mono border-t border-white/5 pt-2">
        <span className="text-muted-foreground">Realized <span style={{ color: realizedColor }}>{data.totalRealizedSol >= 0 ? "+" : ""}{data.totalRealizedSol}</span></span>
        <span className="text-muted-foreground">Open <span style={{ color: unrealColor }}>{data.unrealizedSol >= 0 ? "+" : ""}{data.unrealizedSol}</span></span>
      </div>
      <div className="flex justify-center gap-4 text-xs font-mono">
        <span className="text-neon">{data.wins}W</span>
        <span className="text-danger">{data.losses}L</span>
        <span className="text-muted-foreground">{data.closedPositions} closed · {data.tradesAnalyzed} swaps</span>
      </div>
      {data.openPositions?.length > 0 && (
        <div className="space-y-1 pt-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open bags (unrealized)</div>
          {data.openPositions.map((p) => (
            <div key={p.mint} className="flex justify-between text-xs font-mono">
              <Link to={`/coin/${p.mint}`} data-testid={`pnl-token-${p.mint}`} className="text-muted-foreground truncate hover:text-neon transition-colors" title="View chart">💼 {p.symbol}</Link>
              <span style={{ color: p.unrealizedSol >= 0 ? "#00E599" : "#FF3366" }}>
                {p.unrealizedSol >= 0 ? "+" : ""}{p.unrealizedSol} SOL
              </span>
            </div>
          ))}
        </div>
      )}
      {(data.topWinners.length > 0 || data.topLosers.length > 0) && (
        <div className="space-y-1 pt-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Closed trades</div>
          {data.topWinners.map((p) => (
            <div key={p.mint} className="flex justify-between text-xs font-mono">
              <Link to={`/coin/${p.mint}`} data-testid={`pnl-token-${p.mint}`} className="text-muted-foreground truncate hover:text-neon transition-colors" title="View chart">🏆 {p.symbol}</Link>
              <span className="text-neon">+{p.realizedSol} SOL</span>
            </div>
          ))}
          {data.topLosers.map((p) => (
            <div key={p.mint} className="flex justify-between text-xs font-mono">
              <Link to={`/coin/${p.mint}`} data-testid={`pnl-token-${p.mint}`} className="text-muted-foreground truncate hover:text-neon transition-colors" title="View chart">💀 {p.symbol}</Link>
              <span className="text-danger">{p.realizedSol} SOL</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
        Tap a token to open its chart · Total = realized + unrealized (open bags marked to live price, SOL-denominated). Approximate — not financial advice.
      </p>
    </div>
  );
};

export default function SmartMoney() {
  return (
    <HeliusGate feature="Smart Money">
      <SmartMoneyInner />
    </HeliusGate>
  );
}

function SmartMoneyInner() {
  const clientId = getClientId();
  const qc = useQueryClient();
  const { requireAuth } = useAuth();
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");

  const configQ = useQuery({ queryKey: ["smConfig"], queryFn: fetchSmartMoneyConfig, refetchInterval: 60000 });
  const walletsQ = useQuery({ queryKey: ["trackedWallets", clientId], queryFn: () => fetchTrackedWallets(clientId) });
  const feedQ = useQuery({
    queryKey: ["walletFeed", clientId],
    queryFn: () => fetchWalletFeed(clientId),
    refetchInterval: 30000,
  });

  const addMut = useMutation({
    mutationFn: () => addTrackedWallet(clientId, address.trim(), label.trim()),
    onSuccess: () => {
      toast.success("Wallet added — backfilling recent trades");
      setAddress(""); setLabel("");
      qc.invalidateQueries({ queryKey: ["trackedWallets", clientId] });
      qc.invalidateQueries({ queryKey: ["walletFeed", clientId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to add wallet"),
  });

  const removeMut = useMutation({
    mutationFn: (addr) => removeTrackedWallet(clientId, addr),
    onSuccess: () => {
      toast.success("Wallet removed");
      qc.invalidateQueries({ queryKey: ["trackedWallets", clientId] });
      qc.invalidateQueries({ queryKey: ["walletFeed", clientId] });
    },
    onError: () => toast.error("Failed to remove wallet"),
  });

  const wallets = walletsQ.data?.wallets || [];
  const trades = feedQ.data?.trades || [];
  const heliusConfigured = configQ.data?.heliusConfigured;
  const [expanded, setExpanded] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Fetch PnL for every tracked wallet so we can auto-rank by score (server-cached 10min)
  const pnlResults = useQueries({
    queries: wallets.map((w) => ({
      queryKey: ["walletPnl", clientId, w.address],
      queryFn: () => fetchWalletPnl(clientId, w.address),
      staleTime: 30 * 60 * 1000,
      enabled: !!heliusConfigured,
    })),
  });
  const pnlByAddr = {};
  wallets.forEach((w, i) => { pnlByAddr[w.address] = pnlResults[i]?.data; });
  const anyPnlLoading = pnlResults.some((r) => r.isLoading);
  const rankedWallets = [...wallets].sort(
    (a, b) => (pnlByAddr[b.address]?.score ?? -1) - (pnlByAddr[a.address]?.score ?? -1)
  );

  const selectedLabel = wallets.find((w) => w.address === selectedWallet);
  const dbSelected = selectedWallet ? trades.filter((t) => t.walletAddress === selectedWallet) : trades;
  const selPnl = selectedWallet ? pnlByAddr[selectedWallet] : null;
  // Fallback: if the DB has no recorded trades for the selected wallet, show the live
  // swaps from its PnL analysis (already fetched/cached) so the feed matches the pulldown.
  const usingLiveFallback = !!selectedWallet && dbSelected.length === 0 && (selPnl?.recentTrades?.length || 0) > 0;
  const filteredTrades = usingLiveFallback
    ? selPnl.recentTrades.map((t) => ({ ...t, walletAddress: selectedWallet, walletLabel: selectedLabel?.label || short(selectedWallet) }))
    : dbSelected;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
            <UsersThreeIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">SMART MONEY</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Watch proven wallets (grab them off Axiom). We poll their on-chain swaps every 2 minutes via Helius and stream
          their <span className="text-neon font-semibold">buys & sells</span> here — plus Telegram pings if you've set them up.
        </p>
        <Link
          to="/wallet-buys"
          data-testid="goto-wallet-buys-link"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 border border-neon/40 bg-neon/[0.06] text-neon font-semibold text-sm hover:bg-neon/15 transition-colors"
        >
          <ChartLineUpIcon size={16} weight="bold" /> Go to your Smart Money Wallet Buys page <ArrowUpRightIcon size={14} weight="bold" />
        </Link>
      </div>

      {!configQ.isLoading && !heliusConfigured && (
        <div className="border border-warn/30 bg-warn/5 p-4 flex gap-3">
          <LockKeyIcon size={20} className="text-warn shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">Helius isn't connected — wallet tracking is disabled until an API key is added.</p>
        </div>
      )}

      {!configQ.isLoading && heliusConfigured && configQ.data?.rateLimited && (
        <div data-testid="helius-ratelimit-banner" className="border border-warn/40 bg-warn/[0.07] p-4 flex gap-3">
          <WarningIcon size={20} weight="fill" className="text-warn shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-bold text-sm text-warn">Wallet tracking paused — data limit reached</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Our on-chain data provider (Helius) has hit its usage limit, so new trades aren't being fetched right now.
              Already-recorded trades still show below. Tracking resumes automatically once capacity frees up.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Wallets column */}
        <div className="space-y-4">
          <div className="border border-white/10 bg-card p-5">
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <WalletIcon size={20} /> Track a wallet
            </h2>
            <div className="space-y-3">
              <input
                data-testid="wallet-address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Solana wallet address"
                className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-neon/50 transition-colors"
              />
              <input
                data-testid="wallet-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (e.g. Axiom Whale #1)"
                className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-neon/50 transition-colors"
              />
              <button
                data-testid="add-wallet-btn"
                onClick={() => { if (requireAuth("track wallets")) addMut.mutate(); }}
                disabled={!address.trim() || addMut.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-40"
              >
                {addMut.isPending ? <SpinnerGapIcon size={16} className="animate-spin" /> : <PlusIcon size={16} weight="bold" />}
                Add wallet
              </button>
            </div>
          </div>

          <div className="border border-white/10 bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                Tracking {wallets.length} wallet{wallets.length === 1 ? "" : "s"}
                {wallets.length > 1 && (
                  <span className="text-neon normal-case tracking-normal flex items-center gap-1">
                    · ranked by score {anyPnlLoading && <SpinnerGapIcon size={11} className="animate-spin" />}
                  </span>
                )}
              </div>
              {wallets.length > 0 && (
                <button
                  data-testid="select-all-wallets"
                  onClick={() => setSelectedWallet(null)}
                  className={`text-[11px] font-mono px-2 py-1 border transition-colors ${
                    selectedWallet === null ? "bg-neon/10 border-neon/40 text-neon" : "border-white/10 text-muted-foreground hover:text-white"
                  }`}
                >
                  All
                </button>
              )}
            </div>
            {wallets.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No wallets yet. Add one above.</p>
            ) : (
              <div className="space-y-2">
                {rankedWallets.map((w, idx) => {
                  const active = selectedWallet === w.address;
                  const pnl = pnlByAddr[w.address];
                  const hasScore = pnl && pnl.closedPositions > 0;
                  const copyWorthy = hasScore && pnl.score >= 60;
                  return (
                  <div
                    key={w.address}
                    data-testid={`wallet-item-${w.address}`}
                    className={`border transition-colors cursor-pointer ${active ? "border-neon/50 bg-neon/[0.04]" : "border-white/5 hover:border-white/20"}`}
                  >
                    <div className="flex items-center justify-between gap-2 p-3" onClick={() => setSelectedWallet(active ? null : w.address)}>
                      <div className="min-w-0 flex items-center gap-2.5">
                        <span className="font-mono text-xs text-muted-foreground w-4 text-right shrink-0">{idx + 1}</span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`font-medium text-sm truncate ${active ? "text-neon" : ""}`}>{w.label || short(w.address)}</span>
                            {copyWorthy && (
                              <span data-testid={`copyworthy-${w.address}`} className="shrink-0 text-[9px] font-mono px-1.5 py-0.5 bg-neon/10 border border-neon/30 text-neon tracking-wide">🔥 COPY</span>
                            )}
                          </div>
                          <a
                            href={`https://solscan.io/account/${w.address}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono text-xs text-muted-foreground hover:text-neon flex items-center gap-1"
                          >
                            {short(w.address)} <ArrowSquareOutIcon size={11} />
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {hasScore && (
                          <span className="font-mono text-sm font-bold mr-1" style={{ color: pnlScoreColor(pnl.score) }} title={`Win rate ${pnl.winRate}% · Total ${pnl.totalPnlSol} SOL`}>
                            {pnl.score}
                          </span>
                        )}
                        <button
                          data-testid={`pnl-btn-${w.address}`}
                          onClick={(e) => { e.stopPropagation(); setExpanded(expanded === w.address ? null : w.address); }}
                          className={`p-2 transition-colors ${expanded === w.address ? "text-neon" : "text-muted-foreground hover:text-white"}`}
                          title="Analyze PnL / win-rate"
                        >
                          <ChartBarIcon size={16} weight={expanded === w.address ? "fill" : "regular"} />
                        </button>
                        <button
                          data-testid={`remove-wallet-${w.address}`}
                          onClick={(e) => { e.stopPropagation(); if (selectedWallet === w.address) setSelectedWallet(null); removeMut.mutate(w.address); }}
                          className="p-2 text-muted-foreground hover:text-danger transition-colors"
                        >
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    </div>
                    {expanded === w.address && (
                      <div className="px-3 pb-3 border-t border-white/5" data-testid={`pnl-panel-${w.address}`}>
                        <WalletPnL clientId={clientId} address={w.address} />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Feed column */}
        <div className="lg:col-span-2 border border-white/10 bg-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="font-heading font-bold text-lg truncate">
                {selectedWallet ? `${selectedLabel?.label || short(selectedWallet)} · Trades` : "Live Trade Feed"}
              </h2>
              {selectedWallet && (
                <button
                  data-testid="clear-wallet-filter"
                  onClick={() => setSelectedWallet(null)}
                  className="text-[11px] font-mono px-2 py-0.5 border border-white/10 text-muted-foreground hover:text-white transition-colors shrink-0"
                >
                  Show all
                </button>
              )}
            </div>
            <span className="flex items-center gap-2 text-xs font-mono text-muted-foreground shrink-0">
              <span className="w-2 h-2 rounded-full bg-neon animate-pulse" /> auto-refresh 30s
            </span>
          </div>
          {feedQ.isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
              <SpinnerGapIcon size={28} className="animate-spin text-neon" />
              <span className="font-mono text-sm">Loading trades…</span>
            </div>
          ) : filteredTrades.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground font-mono text-sm px-6">
              {selectedWallet
                ? "No trades recorded for this wallet yet."
                : "No trades yet. Add a wallet — we'll backfill its recent swaps and stream new ones."}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredTrades.map((t) => {
                const buy = t.action === "buy";
                return (
                  <div
                    key={`${t.signature}-${t.tokenMint}`}
                    data-testid={`trade-${t.signature}`}
                    className="flex items-center gap-3 px-5 py-3.5 hover:bg-white/5 transition-colors"
                  >
                    <div className={`w-9 h-9 flex items-center justify-center border shrink-0 ${buy ? "border-neon/30 text-neon" : "border-danger/30 text-danger"}`}>
                      {buy ? <ArrowDownRightIcon size={18} weight="bold" /> : <ArrowUpRightIcon size={18} weight="bold" />}
                    </div>
                    {t.tokenImage ? (
                      <img src={t.tokenImage} alt="" className="w-8 h-8 object-cover border border-white/10" onError={(e) => (e.target.style.display = "none")} />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">
                        <span className="font-semibold" style={{ color: buy ? "#00E599" : "#FF3366" }}>{buy ? "Bought" : "Sold"}</span>{" "}
                        <span className="font-heading font-bold">{t.tokenSymbol}</span>
                        <span className="text-muted-foreground"> · {t.walletLabel}</span>
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {Number(t.tokenAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })} {t.tokenSymbol} · ~{t.solAmount} SOL · {t.source}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <QuickBuyLink
                        coin={{ address: t.tokenMint, url: `https://dexscreener.com/solana/${t.tokenMint}` }}
                        testid={`trade-buy-${t.signature}`}
                      />
                      <a
                        data-testid={`trade-dexscreener-${t.signature}`}
                        href={`https://dexscreener.com/solana/${t.tokenMint}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Open chart on DexScreener"
                        className="p-1.5 text-muted-foreground hover:text-neon transition-colors"
                      >
                        <ChartLineUpIcon size={16} weight="bold" />
                      </a>
                      <a
                        data-testid={`trade-solscan-${t.signature}`}
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        title="View transaction on Solscan"
                        className="p-1.5 text-muted-foreground hover:text-white transition-colors"
                      >
                        <ArrowSquareOutIcon size={16} />
                      </a>
                      <span className="font-mono text-xs text-muted-foreground ml-1">{timeAgo(t.timestamp)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
